import API from '../../../../services/api-middleware';
import { tableFunctionsProps } from '../../user/pages/users/user.props';
import { LlmSavePayloadInput, normalizeLlmModels } from '../model-utils';
import { llmDetailsProps, providerDetailsProps } from '../pages/llm.props';

function withNormalizedModelList<T extends { data?: { data?: { models?: unknown[] } } }>(
  response: T,
): T {
  const models = response?.data?.data?.models;
  if (!Array.isArray(models) || !response.data?.data) {
    return response;
  }
  return {
    ...response,
    data: {
      ...response.data,
      data: {
        ...response.data.data,
        models: normalizeLlmModels(models as Parameters<typeof normalizeLlmModels>[0]),
      },
    },
  };
}

const baseUrl = process.env.REACT_APP_LLM_SERVER_URL;
const appBaseUrl = process.env.REACT_APP_USER_SERVER_URL;

// get providers list
export const getProviders = async () => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).get(`providers`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export type LlmModelsListParams = tableFunctionsProps & {
  routing_band?: number;
  provider_id?: number;
  model_status?: string;
};

function buildModelsListQuery(params: LlmModelsListParams, accountId: number): string {
  const query = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
    search: params.search ?? '',
    sort_col: params.sort_col,
    sort_order: params.sort_order,
    account_id: String(accountId),
  });
  if (params.routing_band != null && params.routing_band >= 1 && params.routing_band <= 5) {
    query.set('routing_band', String(params.routing_band));
  }
  if (params.provider_id != null && params.provider_id > 0) {
    query.set('provider_id', String(params.provider_id));
  }
  if (params.model_status) {
    query.set('model_status', params.model_status);
  }
  return query.toString();
}

// get models list
export const getModels = async (payload: LlmModelsListParams) => {
  try {
    const idd = localStorage.getItem('user_details');
    const id = JSON.parse(idd!);
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const qs = buildModelsListQuery(payload, id?.account_id);
    const response = await API(baseUrl).get(`models?${qs}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return withNormalizedModelList(response);
  } catch (error) {
    throw error;
  }
};

// get all models list
export const getAllModels = async () => {
  try {
    const idd = localStorage.getItem('user_details');
    const id = idd ? JSON.parse(idd) : null;
    const accountId = id?.account_id;

    // If account_id is not available, don't make the API call
    if (!accountId) {
      return { status: 200, data: { data: { models: [] } } };
    }

    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).get(`models?all=True&account_id=${accountId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return withNormalizedModelList(response);
  } catch (error) {
    throw error;
  }
};

// create new role
export const createModel = async (payload: llmDetailsProps | LlmSavePayloadInput) => {
  const data = { ...payload };
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).post(`models`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// edit existing model
export const editModel = async (payload: llmDetailsProps | LlmSavePayloadInput, id: number) => {
  const data = {
    ...payload,
    id,
  };
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).put(`models`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// create new provider
export const createProvider = async (payload: providerDetailsProps) => {
  const data = { ...payload };
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).post(`providers`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// delete models
export const deleteModel = async (id: Array<number>) => {
  const data = {
    id,
  };
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).delete(`models`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// get gemini keys list
export const getGeminiModels = async () => {
  try {
    const userId = localStorage.getItem('user_id');
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(appBaseUrl).get(`gemini-models?user_id=${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};
